import { EnumPraticaDtoStato } from '@framework/api-client/api/backoffice/schemas/enumPraticaDtoStato.ts';
import { type GetBackProcedimentiParams } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiParams.ts';
import { GetBackProcedimentiSettore } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiSettore.ts';
import { GetBackProcedimentiTipologia } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiTipologia.ts';
import { SortDirection } from '@framework/api-client/api/backoffice/schemas/sortDirection.ts';
import { yup } from '@framework/validation/initYup.ts';
import { type NullableObject } from '@framework/validation/NullableObject.ts';
import dayjs from 'dayjs';
import {
  type ISemanticFieldSearch,
  SemanticFieldSearchOperator,
} from '../../semantic-tags/ISemanticFieldSearch.ts';

function normalizeDate(date: Date) {
  if (!date) return undefined;
  return dayjs(date).format('YYYY-MM-DD HH:mm');
}

/**
 * I filtri più recenti hanno adottato il formato data senza orario.
 */
function normalizeDateNoTime(date: Date) {
  if (!date) return undefined;
  return dayjs(date).format('YYYY-MM-DD');
}

export const SemanticTagFilterSchema: yup.ObjectSchema<ISemanticFieldSearch> =
  yup
    .object({
      chiave: yup.string().notRequired().label('chiave'),
      operatore: yup
        .string()
        .required()
        .oneOfEnum(SemanticFieldSearchOperator)
        .label('operatore'),
      valore: yup.mixed(),
    })
    .required()
    .noUnknown();

export const procedureGetQueryParamsSchema: yup.ObjectSchema<
  NullableObject<GetBackProcedimentiParams>
> = yup
  .object({
    numero: yup.string().notRequired().label('Numero procedimento'),
    tipo: yup.string().notRequired().label('Tipo'),
    stato: yup.string().notRequired(),
    dataAvvioDal: yup
      .string()
      .notRequired()
      .transform(value => normalizeDate(value))
      .label('Data avvio dal'),
    dataAvvioAl: yup
      .string()
      .notRequired()
      .transform(value => normalizeDate(value))
      .label('Data avvio al'),
    ufficioAvvio: yup.string().notRequired().label('Ufficio avvio'),
    responsabile: yup.string().notRequired().label('Utente'),
    ufficioResponsabile: yup
      .string()
      .notRequired()
      .label('Ufficio responsabile'),
    oggetto: yup.string().notRequired().label('Oggetto'),
    valoriRicercaEstesa: yup
      .string()
      .notRequired()
      .label('Valori ricerca estesa'),
    progressivoAnno: yup
      .number()
      .notRequired()
      .min(0)
      .label('Progressivo anno'),
    progressivoId: yup
      .number()
      .notRequired()
      .min(0)
      .label('Progressivo numero'),
    progressivo: yup.string().notRequired().label('Progressivo'),
    idUfficioAvvio: yup.string().notRequired().label('Ufficio avvio'),
    idUfficioResponsabile: yup
      .string()
      .notRequired()
      .label('Ufficio responsabile'),
    idIstanzaProcedimento: yup
      .string()
      .notRequired()
      .label('Istanza procedimento'),
    nomeTitolare: yup.string().notRequired().label('Nome titolare'),
    cognomeTitolare: yup.string().notRequired().label('Cognome titolare'),
    registrazioneData: yup.string().notRequired().label('Data registrazione'),
    procedimentoFase: yup.string().notRequired().label('Fase procedimento'),
    registrazioneNumero: yup
      .number()
      .integer()
      .positive()
      .notRequired()
      .label('Numero registrazione'),
    richiestaRegistrazioneData: yup
      .string()
      .notRequired()
      .label('Data protocollo richiesta'),
    richiestaRegistrazioneNumero: yup
      .string()
      .notRequired()
      .label('Numero protocollo richiesta'),
    denominazioneTipoGruppoAllegato: yup
      .string()
      .notRequired()
      .label('Denominazione tipo gruppo allegato'),
    denominazioneTipoAllegato: yup
      .string()
      .notRequired()
      .label('Denominazione tipo allegato'),
    page: yup.number().notRequired().default(1).min(1),
    pageSize: yup.number().notRequired().default(10),
    sortBy: yup.string().optional().default('DATA_ULTIMA_MODIFICA'),
    sortDirection: yup
      .string()
      .oneOfEnum(SortDirection)
      .notRequired()
      .default(SortDirection.DESC),
    settore: yup
      .string()
      .optional()
      .oneOfEnum(GetBackProcedimentiSettore)
      .label('settore'),
    // L'endpoint si aspetta una stringa che rappresenta un array di oggetti "RicercaCampoSemantico"
    campiSemantici: yup.string().notRequired(),
    cfUtenteCoinvoltoLavorazione: yup
      .string()
      .notRequired()
      .label('Utente in lavorazione'),
    tipologia: yup
      .string()
      .oneOfEnum(GetBackProcedimentiTipologia)
      .notRequired()
      .label('Tipologia'),

    // ## PRATICHE ##
    procedimentoAvvioIdentificativo: yup
      .string()
      .notRequired()
      .label('ID procedimento di avvio'),
    procedimentoAvvioDenominazione: yup
      .string()
      .notRequired()
      .label('Denominazione procedimento di avvio'),
    procedimentoAvvioFase: yup
      .string()
      .notRequired()
      .label('Fase procedimento di avvio'),
    procedimentoAvvioDataInvioDal: yup
      .string()
      .notRequired()
      .transform(value => normalizeDateNoTime(value))
      .label('Data invio procedimento di avvio dal'),
    procedimentoAvvioDataInvioAl: yup
      .string()
      .notRequired()
      .transform(value => normalizeDateNoTime(value))
      .label('Data invio procedimento di avvio al'),
    statoPratica: yup
      .string()
      .oneOfEnum(EnumPraticaDtoStato)
      .notRequired()
      .label('Pratica: stato'),
    richiestaAvvioTipoDomanda: yup
      .string()
      .notRequired()
      .label('ID tipo domanda di avvio'),
    richiestaAvvioIdentificativo: yup
      .string()
      .notRequired()
      .label('ID richiesta di avvio'),
    richiestaAvvioNumeroRichiesta: yup
      .string()
      .notRequired()
      .label('Numero richiesta di avvio'),
    richiestaAvvioDataInvioDal: yup
      .string()
      .notRequired()
      .transform(value => normalizeDateNoTime(value))
      .label('Data avvio richiesta di avvio dal'),
    richiestaAvvioDataInvioAl: yup
      .string()
      .notRequired()
      .transform(value => normalizeDateNoTime(value))
      .label('Data avvio richiesta di avvio al'),
    titolarePrincipaleDenominazioneImpresa: yup
      .string()
      .notRequired()
      .label('Titolare: nome impresa'),
    titolarePrincipalePartitaIva: yup
      .string()
      .notRequired()
      .label('Titolare: P.IVA'),
    titolarePrincipaleNome: yup.string().notRequired().label('Titolare: nome'),
    titolarePrincipaleCognome: yup
      .string()
      .notRequired()
      .label('Titolare: cognome'),
    titolarePrincipaleCodiceFiscale: yup
      .string()
      .notRequired()
      .label('Titolare: codice fiscale'),
    titolarePrincipaleCodiceREAImpresa: yup
      .string()
      .notRequired()
      .label('Titolare: codice REA impresa'),
    idIstanzaProcedimentoPratica: yup
      .string()
      .notRequired()
      .label('ID istanza pratica'),
    titolarePrincipaleCodiceFiscaleImpresa: yup
      .string()
      .notRequired()
      .label('Titolare: codice fiscale impresa'),
    soggettoRichiestaNome: yup
      .string()
      .notRequired()
      .label('Soggetto richiesta: nome'),
    soggettoRichiestaCognome: yup
      .string()
      .notRequired()
      .label('Soggetto richiesta: cognome'),
    soggettoRichiestaCodiceFiscale: yup
      .string()
      .notRequired()
      .label('Soggetto richiesta: codice fiscale'),
    idCartella: yup.string().notRequired().label('Cartella'),
    codiceUfficio: yup.string().notRequired().label('Codice ufficio'),
  })
  .noUnknown()
  .required();
